import React from 'react';
import { IconV1, Box, Flex, TextV2 } from '@withjoy/joykit';
import {
  StyledImage,
  StyledLink,
  DateText,
  EditNoteBox,
  EditPhotoButton,
  EditNoteButton,
  StyledTextBox,
  EditPhotoBox,
  CoupleWrapper,
  NoteAndPhotoRow,
  StyledPhotoBox,
  DashboardButtonWrapper,
  styles,
  StyledRoot,
  StyledTitle,
  EmptyImage
} from './CoupleHeader.styles';
import { useCoupleHeaderController } from './CoupleHeader.controller';
import { PageNoteText } from './PageNoteText';
import { ReactComponent as CameraIcon } from '@assets/icons/camera-outline.svg';
import { DashboardButton, NavBar } from '@apps/registry/guest/components';
import { DesignLayoutType, EventPageFragment, EventType } from '@graphql/generated';
import { PageNoteDialog } from '../PageNoteDialog';
import { brannanConfig } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan';
import { UserProfile } from '@shared/components/AuthProvider';
import { PhotoMenu } from '@shared/components/PhotoMenu';
import { TempSpinner } from '@shared/components/TempSpinner';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useEventType } from '@shared/utils/eventType';
import { useRegistryGuestTranslations } from '../../GuestRegistry.i18n';

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

interface BrannanHeaderProps {
  website: string;
  font: string;
  emptyNote: boolean;
  layout?: DesignLayoutType;
  primaryTextColor?: string;
  pages?: ReadonlyArray<EventPageFragment>;
  currentUser: UserProfile;
  onHandleLogout: () => void;
  isAdmin: boolean;
  isNavBarVisible: boolean;
}

interface Props
  extends Readonly<{
    website: string;
    eventDate?: string | null;
    pageNote?: string | null;
    isAdmin: boolean;
    registryPhoto?: string;
    eventId: string;
    firebaseId?: string;
    font?: string;
    pages?: ReadonlyArray<EventPageFragment>;
    layout?: DesignLayoutType;
    primaryTextColor?: string;
    isNative: boolean;
  }> {}

const BrannanHeader = ({ website, font, emptyNote, layout, pages, currentUser, onHandleLogout, primaryTextColor, isAdmin, isNavBarVisible }: BrannanHeaderProps) => {
  const { eventType } = useEventType();
  const { getCoupleHeaderTranslations } = useRegistryGuestTranslations();
  const { eventInfo } = useEventInfo();
  const translations = getCoupleHeaderTranslations().defaultName;
  const defaultDisplayName = translations[eventType ?? EventType.general]();

  return (
    <>
      <StyledRoot position={'relative'} minHeight={[291, 200, null, 'initial']} {...componentTitleBanner.prop}>
        <StyledTitle __css={styles.title}>
          <TextV2 typographyVariant={'display6'} fontFamily={font} fontWeight={400} marginBottom={[7, null, emptyNote ? 9 : 56]}>
            {eventInfo?.eventDisplayName ?? defaultDisplayName}
          </TextV2>
        </StyledTitle>
      </StyledRoot>
      <Flex justifyContent={'space-between'} alignItems={'center'} paddingX={['10px', null, '0px']} width={'100%'}>
        {isNavBarVisible && (
          <NavBar layout={layout} pages={pages} font={font} eventHandle={website} currentUser={currentUser} onHandleLogout={onHandleLogout} primaryTextColor={primaryTextColor} />
        )}
      </Flex>

      <Flex margin="0 auto">
        {/* Manage Registry Button */}
        {isAdmin && (
          <DashboardButtonWrapper __css={styles.dashboardButtonWrapperBrannan}>
            <DashboardButton website={website} />
          </DashboardButtonWrapper>
        )}
      </Flex>
    </>
  );
};

export const CoupleHeader: React.FC<Props> = ({
  eventDate,
  pageNote,
  isAdmin,
  registryPhoto,
  website,
  firebaseId,
  eventId,
  font = 'Austin News Headline',
  pages,
  layout,
  primaryTextColor,
  isNative
}) => {
  const {
    handleOnUpdatePhoto,
    handleDeletePhoto,
    onEditPageNote,
    onContinueReading,
    isAtLeastTablet,
    translations,
    showFullPageNote,
    photoRef,
    photo,
    currentUser,
    onHandleLogout,
    isMobileOrTablet,
    openPageNoteDialogAsAdmin,
    isPageNoteDialogOpen,
    closePageNoteDialog,
    isNavBarVisible,
    isUploadingPhoto,
    isImageLoading,
    handleImageLoad
  } = useCoupleHeaderController({
    eventId,
    firebaseId,
    isAdmin,
    website,
    registryPhoto,
    pages
  });

  const photoExists = !!photo;
  const emptyNote = !pageNote?.length;
  const leftAlignInTextbox = isAtLeastTablet;
  const photoMenuButton = (
    <EditPhotoButton isMobileOrTablet>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Box marginRight={3} display="flex" justifyContent="center" alignItems="center">
          <CameraIcon />
        </Box>
        {photoExists ? translations.photoButton : translations.addPhotoButton}
      </Box>
    </EditPhotoButton>
  );
  const photoMenuEl = (
    <PhotoMenu handleUpdatePhoto={handleOnUpdatePhoto} handleDeletePhoto={handleDeletePhoto} photoExists>
      {photoMenuButton}
    </PhotoMenu>
  );
  return (
    <>
      {!isNative && (
        <BrannanHeader
          layout={layout}
          pages={pages}
          font={font}
          currentUser={currentUser}
          onHandleLogout={onHandleLogout}
          primaryTextColor={primaryTextColor}
          emptyNote={emptyNote}
          isAdmin={isAdmin}
          website={website}
          isNavBarVisible={isNavBarVisible}
        />
      )}
      <CoupleWrapper margin="0 auto">
        <NoteAndPhotoRow>
          <StyledTextBox fullWidth={!isAdmin && !photoExists}>
            <Box display="flex" flexDirection="column">
              <Box width="100%" marginBottom={emptyNote ? 6 : 5}>
                {eventDate && !isNative && (
                  <DateText typographyVariant="hed4" textAlign={leftAlignInTextbox ? 'left' : 'center'}>
                    {eventDate}
                  </DateText>
                )}
              </Box>
              <EditNoteBox>
                <PageNoteText
                  noteText={pageNote}
                  textAlign="left"
                  showFullPageNote={showFullPageNote}
                  key={pageNote}
                  renderReadMore={() => (
                    <Box marginTop={[5]} textAlign="left">
                      <StyledLink typographyVariant="body3" color="mono14" textDecoration="underline" onClick={onContinueReading}>
                        {translations.expandText}
                      </StyledLink>
                    </Box>
                  )}
                />
                {isAdmin && (
                  <Flex justifyContent="flex-end" columnGap={4} marginTop={[4, 0]} position={['initial', 'absolute']} top={['unset', '100%']} left={['unset', '0']}>
                    <EditNoteButton isMobileOrTablet={isMobileOrTablet} onClick={onEditPageNote} emptyNote={emptyNote}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <Box marginRight={3} display="flex" justifyContent="center" alignItems="center">
                          <IconV1 color={'white'} source="edit" />
                        </Box>
                        {!emptyNote ? translations.noteButton : translations.addNoteButton}
                      </Box>
                    </EditNoteButton>
                  </Flex>
                )}
              </EditNoteBox>
            </Box>
          </StyledTextBox>
          {(isAdmin || photoExists) && (
            <StyledPhotoBox position="relative" maxWidth="100%" height="100%">
              <Box opacity={isUploadingPhoto ? '0.3' : '1'} visibility={isImageLoading ? 'hidden' : 'visible'}>
                {photoExists && <StyledImage ref={photoRef} src={photo} onLoad={handleImageLoad} />}
                {!photoExists && <EmptyImage />}
              </Box>
              {isUploadingPhoto && <TempSpinner position={'absolute'} />}
              {isAdmin && !isNative && (
                <EditPhotoBox>
                  <Box position="absolute" top={['unset', '0']} left={['unset', '0']} bottom={['0', 'unset']} right={['0', 'unset']} margin={6}>
                    {photoMenuEl}
                  </Box>
                </EditPhotoBox>
              )}
            </StyledPhotoBox>
          )}
        </NoteAndPhotoRow>
        <PageNoteDialog isOpen={isPageNoteDialogOpen} onClose={closePageNoteDialog} pageNote={pageNote} isAdmin={openPageNoteDialogAsAdmin} eventHandle={website}></PageNoteDialog>
      </CoupleWrapper>
    </>
  );
};
